import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import TokenLogo from "../../components/TokenLogo";
import PairLogos from "../../components/PairLogos";
import { formatCount, formatPriceUSD, formatUSD } from "../../utils/format";

export const tokenHref = (token) => `/explore/tokens/${token.id}`;
export const poolHref = (pool) => `/explore/pools/${pool.id}`;
export const poolName = (pool) => `${pool.token0.symbol} / ${pool.token1.symbol}`;

// numbers for sorting; a missing USD value (no KOIN price) is null and sorts last
const toNumber = (value) => (value === null || value === undefined ? null : Number(value));

/**
 * USD volume; "—" when there were trades but none through a token with a USD price (volume not tracked)
 */
export const formatVolume = (volumeUSD, txCount) =>
  Number(volumeUSD) === 0 && Number(txCount) > 0 ? "—" : formatUSD(volumeUSD);
const volumeValue = (volumeUSD, txCount) =>
  Number(volumeUSD) === 0 && Number(txCount) > 0 ? null : toNumber(volumeUSD);

const rankColumn = {
  id: "rank",
  label: "#",
  align: "left",
  display: { xs: "none", md: "table-cell" },
  render: (row, rank) => <Typography sx={{ color: "text.secondary" }}>{rank}</Typography>,
};

// the name cell is the row's link: keyboard focus and "open in a new tab" work on it
const NameLink = ({ to, label, children }) => (
  <Link
    component={RouterLink}
    to={to}
    underline="none"
    color="inherit"
    aria-label={label}
    sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, borderRadius: "8px" }}
  >
    {children}
  </Link>
);

export const tokenColumns = [
  rankColumn,
  {
    id: "token",
    label: "Token",
    align: "left",
    sortValue: (token) => token.symbol.toLowerCase(),
    render: (token) => (
      <NameLink to={tokenHref(token)} label={`${token.name} (${token.symbol})`}>
        <TokenLogo token={token} size={32} />
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 600, maxWidth: { xs: 140, sm: 260 } }}>
            {token.name}
          </Typography>
          <Typography variant="body2" noWrap sx={{ color: "text.secondary" }}>
            {token.symbol}
          </Typography>
        </Box>
      </NameLink>
    ),
  },
  {
    id: "price",
    label: "Price",
    sortValue: (token) => toNumber(token.priceUSD),
    render: (token) => formatPriceUSD(token.priceUSD),
  },
  {
    id: "tvl",
    label: "TVL",
    display: { xs: "none", sm: "table-cell" },
    sortValue: (token) => toNumber(token.totalLiquidityUSD),
    render: (token) => formatUSD(token.totalLiquidityUSD),
  },
  {
    id: "volume",
    label: "Total volume",
    display: { xs: "none", md: "table-cell" },
    sortValue: (token) => volumeValue(token.tradeVolumeUSD, token.txCount),
    render: (token) => formatVolume(token.tradeVolumeUSD, token.txCount),
  },
  {
    id: "transactions",
    label: "Transactions",
    display: { xs: "none", md: "table-cell" },
    sortValue: (token) => token.txCount,
    render: (token) => formatCount(token.txCount),
  },
];

export const poolColumns = [
  rankColumn,
  {
    id: "pool",
    label: "Pool",
    align: "left",
    sortValue: (pool) => poolName(pool).toLowerCase(),
    render: (pool) => (
      <NameLink to={poolHref(pool)} label={`${poolName(pool)} pool`}>
        <PairLogos tokens={[pool.token0, pool.token1]} size={28} />
        <Typography noWrap sx={{ fontWeight: 600, maxWidth: { xs: 150, sm: 320 } }}>
          {poolName(pool)}
        </Typography>
      </NameLink>
    ),
  },
  {
    id: "tvl",
    label: "TVL",
    sortValue: (pool) => toNumber(pool.reserveUSD),
    render: (pool) => formatUSD(pool.reserveUSD),
  },
  {
    id: "volume",
    label: "Total volume",
    display: { xs: "none", sm: "table-cell" },
    sortValue: (pool) => volumeValue(pool.volumeUSD, pool.txCount),
    render: (pool) => formatVolume(pool.volumeUSD, pool.txCount),
  },
  {
    id: "transactions",
    label: "Transactions",
    display: { xs: "none", md: "table-cell" },
    sortValue: (pool) => pool.txCount,
    render: (pool) => formatCount(pool.txCount),
  },
];

// search by symbol, name or address
const matches = (token, text) =>
  token.symbol.toLowerCase().includes(text) ||
  token.name.toLowerCase().includes(text) ||
  token.id.toLowerCase() === text;

export const searchTokens = (tokens, query) => {
  const text = query.trim().toLowerCase();
  return text ? tokens.filter((token) => matches(token, text)) : tokens;
};

export const searchPools = (pools, query) => {
  const text = query.trim().toLowerCase();
  return text
    ? pools.filter(
        (pool) =>
          matches(pool.token0, text) ||
          matches(pool.token1, text) ||
          pool.id.toLowerCase() === text ||
          poolName(pool).toLowerCase().includes(text)
      )
    : pools;
};
